import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { DefaultPageLayout } from '../ui';
import { Button } from '../ui/components/Button';
import { Badge } from '../ui/components/Badge';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { MintCountdownTimer } from '../components/mint/MintCountdownTimer';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { useSignInModalStore } from '../store/signInModalStore';
import {
  getRegistrationInfo,
  registerForAllowlist,
  initiateRegistrationPayment,
  getRegistrationPaymentStatus,
  formatDropsToXrp,
  type RegistrationInfo,
  type RegistrationPhaseInfo,
  type RequirementCheckResult,
  type PaidRegistrationStatus,
} from '../api/drops';
import { calculateMintServiceFee } from '../lib/marketplace-constants';
import { WalletSigningModal } from '../components/transactions/WalletSigningModal';
import { type XamanPayloadDetails } from '../components/transactions/XamanSigningModal';
import { toSigningPayload } from '../lib/wallet';
import { FeatherExternalLink } from '@subframe/core';
import { PostRegistrationModal } from '../components/launchpad/PostRegistrationModal';

export function AllowlistRegistrationPage() {
  const { slug } = useParams<{ slug: string }>();
  const { isAuthenticated } = useAuthStore();
  const { success, error: showError } = useNotificationStore();
  const openSignIn = useSignInModalStore((st) => st.open);

  const [info, setInfo] = useState<RegistrationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registeredPhase, setRegisteredPhase] = useState<'allowlist' | 'allowlist_2' | null>(null);
  const [twitterUsernames, setTwitterUsernames] = useState<Record<string, string>>({});
  const [twitterFollowClicked, setTwitterFollowClicked] = useState<Record<string, boolean>>({});
  // 'pending' -> 'verifying' (spinner) -> 'verified' (green check)
  const [twitterVerifyStatus, setTwitterVerifyStatus] = useState<Record<string, 'pending' | 'verifying' | 'verified'>>({});
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [paymentState, setPaymentState] = useState<Record<string, 'idle' | 'signing' | 'confirming' | 'confirmed' | 'failed'>>({});
  const [paymentPayload, setPaymentPayload] = useState<XamanPayloadDetails | null>(null);
  const [activePaymentPhase, setActivePaymentPhase] = useState<string | null>(null);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const paymentIdRef = useRef<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const paymentPollRef = useRef<NodeJS.Timeout | null>(null);
  const activePaymentPhaseRef = useRef<string | null>(null);

  const fetchInfo = useCallback(async () => {
    if (!slug) return;
    try {
      setError(null);
      const data = await getRegistrationInfo(slug);
      setInfo(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load registration info');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchInfo();
  }, [fetchInfo, isAuthenticated]);

  const handleRegister = async (phase: 'allowlist' | 'allowlist_2') => {
    if (!slug) return;
    setRegistering(phase);
    try {
      const twitterUsername = twitterUsernames[phase] || undefined;
      const result = await registerForAllowlist(slug, phase, twitterUsername);
      if (result.alreadyRegistered) {
        success('You were already registered for this allowlist!');
      } else {
        setRegisteredPhase(phase);
        setShowSuccessModal(true);
      }
      await fetchInfo();
    } catch (err: any) {
      showError(err.message || 'Failed to register');
    } finally {
      setRegistering(null);
    }
  };

  const handleInitiatePayment = async (phase: 'allowlist' | 'allowlist_2') => {
    if (!slug) return;
    try {
      setPaymentState((prev) => ({ ...prev, [phase]: 'signing' }));
      setPaymentError(null);
      setActivePaymentPhase(phase);
      activePaymentPhaseRef.current = phase;
      const twitterUsername = twitterUsernames[phase] || undefined;
      const quantity = quantities[phase] || 1;
      const result = await initiateRegistrationPayment(slug, phase, quantity, twitterUsername);
      setPaymentId(result.paymentId);
      paymentIdRef.current = result.paymentId;
      setPaymentPayload({
        uuid: result.xaman.uuid,
        qrUrl: result.xaman.qrUrl,
        deepLink: result.xaman.deepLink,
        websocketUrl: result.xaman.websocketUrl,
      });
      // Start polling our backend immediately — ingestion detects on-chain
      // payments faster than Xaman's HTTP status endpoint confirms dispatch
      startPaymentPolling();
    } catch (err: any) {
      setPaymentState((prev) => ({ ...prev, [phase]: 'failed' }));
      setPaymentError(err.message || 'Failed to initiate payment');
      showError(err.message || 'Failed to initiate payment');
    }
  };

  const handlePaymentSigned = () => {
    // Xaman confirmed dispatch — close modal, polling is already running
    setPaymentPayload(null);
    if (activePaymentPhase) {
      setPaymentState((prev) => ({ ...prev, [activePaymentPhase]: 'confirming' }));
    }
  };

  const handlePaymentRejected = () => {
    setPaymentPayload(null);
    if (activePaymentPhase) {
      setPaymentState((prev) => ({ ...prev, [activePaymentPhase]: 'idle' }));
    }
    showError('Payment was rejected');
  };

  const handlePaymentExpired = () => {
    setPaymentPayload(null);
    if (activePaymentPhase) {
      setPaymentState((prev) => ({ ...prev, [activePaymentPhase]: 'idle' }));
    }
    showError('Payment request expired');
  };

  const startPaymentPolling = () => {
    if (paymentPollRef.current) clearInterval(paymentPollRef.current);
    paymentPollRef.current = setInterval(async () => {
      const currentPaymentId = paymentIdRef.current;
      const currentPhase = activePaymentPhaseRef.current;
      if (!slug || !currentPaymentId) return;
      try {
        const status = await getRegistrationPaymentStatus(slug, currentPaymentId);
        if (status.status === 'confirmed') {
          if (paymentPollRef.current) clearInterval(paymentPollRef.current);
          setPaymentPayload(null); // Close Xaman modal if still open
          setPaymentState((prev) => ({
            ...prev,
            [currentPhase!]: 'confirmed',
          }));
          success('Payment confirmed! You are now registered.');
          setRegisteredPhase(currentPhase as 'allowlist' | 'allowlist_2');
          setShowSuccessModal(true);
          await fetchInfo();
        } else if (status.status === 'expired' || status.status === 'failed') {
          if (paymentPollRef.current) clearInterval(paymentPollRef.current);
          setPaymentState((prev) => ({
            ...prev,
            [currentPhase!]: 'failed',
          }));
          setPaymentError(status.errorMessage || 'Payment failed');
          showError(status.errorMessage || 'Payment failed or expired');
        }
      } catch (err) {
        console.error('Failed to poll payment status:', err);
      }
    }, 3000);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (paymentPollRef.current) clearInterval(paymentPollRef.current);
    };
  }, []);

  if (loading) {
    return (
      <DefaultPageLayout>
        <div className="flex items-center justify-center py-24">
          <LoadingSpinner size="lg" />
        </div>
      </DefaultPageLayout>
    );
  }

  if (error || !info) {
    return (
      <DefaultPageLayout>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="p-8 rounded-lg border border-neutral-border bg-default-background text-center">
            <p className="text-body font-body text-subtext-color">
              {error || 'Drop not found'}
            </p>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  if (info.phases.length === 0) {
    return (
      <DefaultPageLayout>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="p-8 rounded-lg border border-neutral-border bg-default-background text-center">
            <p className="text-heading-3 font-heading-3 text-default-font mb-2">
              {info.drop.name}
            </p>
            <p className="text-body font-body text-subtext-color">
              Registration is not currently open for this drop.
            </p>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  const { drop } = info;
  const mintPrice = drop.allowlist_mint_price_drops ?? drop.mint_price_drops;

  return (
    <DefaultPageLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Banner */}
        {drop.banner_url && (
          <div
            className="w-full h-48 rounded-xl bg-cover bg-center mb-6"
            style={{
              backgroundImage: `url(${drop.banner_url})`,
              backgroundPositionY: `${drop.banner_position_y}%`,
            }}
          />
        )}

        {/* Drop Info Header */}
        <div className="flex items-start gap-4 mb-6">
          {drop.thumbnail_url && (
            <img
              src={drop.thumbnail_url}
              alt={drop.name}
              className="w-16 h-16 rounded-lg object-cover border border-neutral-border flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-heading-2 font-heading-2 text-default-font">
              {drop.name}
            </h1>
            <p className="text-body font-body text-subtext-color mt-1">
              Register for the allowlist to secure your mint spot.
            </p>
          </div>
        </div>

        {/* Drop Details Card */}
        <div className="p-4 rounded-lg border border-neutral-border bg-default-background mb-6">
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-body font-body">
            {drop.total_supply && (
              <div>
                <span className="text-subtext-color">Supply: </span>
                <span className="text-default-font">{drop.total_supply.toLocaleString()}</span>
              </div>
            )}
            {mintPrice != null && (
              <div>
                <span className="text-subtext-color">Mint Price: </span>
                <span className="text-default-font">{formatDropsToXrp(mintPrice)}</span>
              </div>
            )}
            {drop.total_supply && drop.minted_count > 0 && (
              <div>
                <span className="text-subtext-color">Minted: </span>
                <span className="text-default-font">{drop.minted_count.toLocaleString()} / {drop.total_supply.toLocaleString()}</span>
              </div>
            )}
          </div>
          {drop.description && (
            <p className="text-body font-body text-subtext-color mt-3 border-t border-neutral-border pt-3">
              {drop.description}
            </p>
          )}
          {(drop.website_url || drop.twitter_handle || drop.discord_url) && (
            <div className="flex items-center gap-4 mt-3 border-t border-neutral-border pt-3">
              {drop.website_url && (
                <a href={drop.website_url} target="_blank" rel="noopener noreferrer" className="text-caption font-caption text-brand-primary hover:underline flex items-center gap-1">
                  Website <FeatherExternalLink className="w-3 h-3" />
                </a>
              )}
              {drop.twitter_handle && (
                <a href={`https://x.com/${drop.twitter_handle}`} target="_blank" rel="noopener noreferrer" className="text-caption font-caption text-brand-primary hover:underline flex items-center gap-1">
                  @{drop.twitter_handle} <FeatherExternalLink className="w-3 h-3" />
                </a>
              )}
              {drop.discord_url && (
                <a href={drop.discord_url} target="_blank" rel="noopener noreferrer" className="text-caption font-caption text-brand-primary hover:underline flex items-center gap-1">
                  Discord <FeatherExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          )}
        </div>

        {/* Phase Cards */}
        <div className="space-y-6">
          {info.phases.map((phase) => (
            <PhaseCard
              key={phase.phase}
              phase={phase}
              isAuthenticated={isAuthenticated}
              registering={registering === phase.phase}
              onRegister={() => handleRegister(phase.phase)}
              onLogin={openSignIn}
              twitterUsername={twitterUsernames[phase.phase] || ''}
              onTwitterUsernameChange={(value) =>
                setTwitterUsernames((prev) => ({ ...prev, [phase.phase]: value }))
              }
              twitterFollowClicked={twitterFollowClicked[phase.phase] || false}
              onTwitterFollowClick={() =>
                setTwitterFollowClicked((prev) => ({ ...prev, [phase.phase]: true }))
              }
              twitterVerifyStatus={twitterVerifyStatus[phase.phase] || 'pending'}
              onTwitterUsernameBlur={() => {
                const phaseKey = phase.phase;
                const username = twitterUsernames[phaseKey]?.trim();
                const clicked = twitterFollowClicked[phaseKey];
                if (clicked && username) {
                  setTwitterVerifyStatus((prev) => ({ ...prev, [phaseKey]: 'verifying' }));
                  setTimeout(() => {
                    setTwitterVerifyStatus((prev) => ({ ...prev, [phaseKey]: 'verified' }));
                  }, 2000);
                }
              }}
              quantity={quantities[phase.phase] || 1}
              onQuantityChange={(q) => setQuantities((prev) => ({ ...prev, [phase.phase]: q }))}
              paymentState={paymentState[phase.phase] || 'idle'}
              paymentError={paymentError}
              onInitiatePayment={() => handleInitiatePayment(phase.phase)}
            />
          ))}
        </div>
      </div>

      {/* Post-Registration Success Modal */}
      {showSuccessModal && registeredPhase && info && (
        <PostRegistrationModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          dropName={info.drop.name}
          phaseName={
            info.phases.find(p => p.phase === registeredPhase)?.phaseName ?? 'Allowlist'
          }
          mintStartAt={
            registeredPhase === 'allowlist'
              ? info.drop.allowlist_start_at
              : info.drop.allowlist_2_start_at
          }
        />
      )}

      {/* Xaman Payment Signing Modal */}
      {paymentPayload && (
        <WalletSigningModal
          open={!!paymentPayload}
          onClose={() => {
            setPaymentPayload(null);
            if (activePaymentPhase) {
              setPaymentState((prev) => ({ ...prev, [activePaymentPhase]: 'idle' }));
            }
          }}
          title="Registration Payment"
          payload={toSigningPayload(paymentPayload)}
          onSuccess={handlePaymentSigned}
          onRejected={handlePaymentRejected}
          onExpired={handlePaymentExpired}
          message="Scan the QR code with your Xaman wallet to complete registration"
        />
      )}
    </DefaultPageLayout>
  );
}

function PhaseCard({
  phase,
  isAuthenticated,
  registering,
  onRegister,
  onLogin,
  twitterUsername,
  onTwitterUsernameChange,
  twitterFollowClicked,
  onTwitterFollowClick,
  twitterVerifyStatus,
  onTwitterUsernameBlur,
  quantity,
  onQuantityChange,
  paymentState,
  paymentError,
  onInitiatePayment,
}: {
  phase: RegistrationPhaseInfo;
  isAuthenticated: boolean;
  registering: boolean;
  onRegister: () => void;
  onLogin: () => void;
  twitterUsername: string;
  onTwitterUsernameChange: (value: string) => void;
  twitterFollowClicked: boolean;
  onTwitterFollowClick: () => void;
  twitterVerifyStatus: 'pending' | 'verifying' | 'verified';
  onTwitterUsernameBlur: () => void;
  quantity: number;
  onQuantityChange: (q: number) => void;
  paymentState: 'idle' | 'signing' | 'confirming' | 'confirmed' | 'failed';
  paymentError: string | null;
  onInitiatePayment: () => void;
}) {
  const spotsText = phase.registrationLimit
    ? `${phase.registrationCount} / ${phase.registrationLimit} spots filled`
    : `${phase.registrationCount} registered`;

  const spotsPercent = phase.registrationLimit
    ? Math.min(100, Math.round((phase.registrationCount / phase.registrationLimit) * 100))
    : null;

  const deadlineDate = phase.deadline ? new Date(phase.deadline) : null;
  const isPastDeadline = deadlineDate && deadlineDate.getTime() <= Date.now();

  const visibleRequirements = phase.requirements?.filter((r) => r.type !== 'paid_registration') || [];
  const hasRequirements = visibleRequirements.length > 0;
  const hasTwitterReq = visibleRequirements.some((r) => r.type === 'twitter_follow');
  const twitterNotVerified = hasTwitterReq && twitterVerifyStatus !== 'verified';
  const requirementsBlocked = hasRequirements && phase.allRequirementsMet === false;

  const isPaid = !!phase.paidRegistration;
  const paidConfig = phase.paidRegistration;

  const totalBaseDrops = isPaid ? paidConfig!.priceDrops * quantity : 0;
  const feeDrops = isPaid ? calculateMintServiceFee(totalBaseDrops, paidConfig!.serviceFeePercent) : 0;
  const totalPaymentDrops = totalBaseDrops + feeDrops;
  const maxQty = isPaid ? Math.min(
    paidConfig!.maxQuantity || Infinity,
    phase.spotsRemaining != null ? phase.spotsRemaining : Infinity
  ) : 1;

  const nonPaymentRequirementsMet = hasRequirements
    ? phase.requirements!.filter(r => r.type !== 'paid_registration').every(r => r.passed)
    : true;

  const canRegister = !isPaid && phase.isOpen && !requirementsBlocked && !twitterNotVerified;

  return (
    <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-body-bold font-body-bold text-default-font">
          {phase.phaseName}
        </h3>
        {phase.isOpen ? (
          <Badge variant="success">Open</Badge>
        ) : (
          <Badge variant="neutral">Closed</Badge>
        )}
      </div>

      {/* Stats */}
      <div className="space-y-3 mb-5">
        {/* Spots counter */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-caption font-caption text-subtext-color">{spotsText}</span>
            {spotsPercent !== null && (
              <span className="text-caption font-caption text-subtext-color">{spotsPercent}%</span>
            )}
          </div>
          {spotsPercent !== null && (
            <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-500 rounded-full transition-all"
                style={{ width: `${spotsPercent}%` }}
              />
            </div>
          )}
        </div>

        {/* Mints per registration */}
        {isPaid ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-caption font-caption text-subtext-color">Mint slots</span>
              <div className="flex items-center border border-neutral-border rounded-lg overflow-hidden bg-default-background">
                <button
                  type="button"
                  onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  className="w-9 h-9 flex items-center justify-center hover:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-default-font"
                >
                  <span className="text-lg font-medium leading-none">−</span>
                </button>
                <input
                  type="number"
                  min={1}
                  max={maxQty}
                  value={quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) {
                      onQuantityChange(Math.max(1, Math.min(val, maxQty)));
                    }
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-10 h-9 text-center text-body-bold font-body-bold text-default-font bg-transparent border-x border-neutral-border focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button
                  type="button"
                  onClick={() => onQuantityChange(Math.min(maxQty, quantity + 1))}
                  disabled={quantity >= maxQty}
                  className="w-9 h-9 flex items-center justify-center hover:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-default-font"
                >
                  <span className="text-lg font-medium leading-none">+</span>
                </button>
              </div>
              {paidConfig!.maxQuantity && (
                <span className="text-caption font-caption text-subtext-color">max {paidConfig!.maxQuantity}</span>
              )}
            </div>
            <div className="text-caption font-caption text-subtext-color space-y-0.5">
              <div>{formatDropsToXrp(paidConfig!.priceDrops)} x {quantity} = {formatDropsToXrp(totalBaseDrops)}</div>
              {feeDrops > 0 && <div>+ {formatDropsToXrp(feeDrops)} service fee</div>}
              <div className="text-default-font font-body-bold">Total: {formatDropsToXrp(totalPaymentDrops)}</div>
            </div>
          </div>
        ) : (
          <p className="text-caption font-caption text-subtext-color">
            {phase.defaultMints} mint {phase.defaultMints === 1 ? 'spot' : 'spots'} per registration
          </p>
        )}
      </div>

      {/* Countdown - only show when registration is still open */}
      {deadlineDate && !isPastDeadline && phase.isOpen && (
        <div className="mb-5">
          <MintCountdownTimer targetDate={deadlineDate} label="Registration closes in" />
        </div>
      )}

      {/* Requirements Checklist */}
      {hasRequirements && (
        <div className="mb-5 p-4 rounded-lg border border-neutral-border bg-default-background">
          <p className="text-caption-bold font-caption-bold text-default-font mb-3">
            Requirements
          </p>
          <div className="space-y-2.5">
            {visibleRequirements.map((req, i) => (
              <RequirementRow
                key={`${req.type}-${i}`}
                requirement={req}
                isRegistered={phase.userStatus === 'registered'}
                twitterUsername={twitterUsername}
                onTwitterUsernameChange={onTwitterUsernameChange}
                twitterFollowClicked={twitterFollowClicked}
                onTwitterFollowClick={onTwitterFollowClick}
                twitterVerifyStatus={twitterVerifyStatus}
                onTwitterUsernameBlur={onTwitterUsernameBlur}
              />
            ))}
          </div>
        </div>
      )}

      {/* Action */}
      {phase.userStatus === 'registered' ? (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <div>
            <p className="text-body-bold font-body-bold text-green-700 dark:text-green-400">
              You're registered!
            </p>
            <p className="text-caption font-caption text-green-600 dark:text-green-500">
              {phase.userMintLimit
                ? `${phase.userMintLimit} mint ${phase.userMintLimit === 1 ? 'spot' : 'spots'} reserved`
                : 'Unlimited mints'}
            </p>
          </div>
        </div>
      ) : phase.userStatus === 'not_logged_in' ? (
        <Button onClick={onLogin} className="w-full">
          Log in to Register
        </Button>
      ) : isPaid ? (
        // Paid registration flow
        paymentState === 'signing' ? (
          <Button loading disabled className="w-full">Preparing Payment...</Button>
        ) : paymentState === 'confirming' ? (
          <Button loading disabled className="w-full">Confirming payment on-chain...</Button>
        ) : paymentState === 'failed' ? (
          <div className="space-y-2">
            {paymentError && (
              <p className="text-caption font-caption text-red-500">{paymentError}</p>
            )}
            <Button onClick={onInitiatePayment} className="w-full">
              Retry Payment
            </Button>
          </div>
        ) : phase.isOpen && nonPaymentRequirementsMet && !twitterNotVerified ? (
          <Button onClick={onInitiatePayment} className="w-full">
            Pay {formatDropsToXrp(totalPaymentDrops)} to Register
          </Button>
        ) : phase.isOpen ? (
          <Button disabled className="w-full">
            {!nonPaymentRequirementsMet
              ? 'Requirements Not Met'
              : twitterVerifyStatus === 'verifying'
                ? 'Verifying...'
                : !twitterFollowClicked
                  ? 'Follow on X to Continue'
                  : 'Enter X Username to Register'}
          </Button>
        ) : (
          <Button disabled className="w-full">
            {phase.closedReason || 'Registration Closed'}
          </Button>
        )
      ) : canRegister ? (
        <Button
          onClick={onRegister}
          loading={registering}
          disabled={registering}
          className="w-full"
        >
          Register for Allowlist
        </Button>
      ) : phase.isOpen ? (
        <Button disabled className="w-full">
          {requirementsBlocked
            ? 'Requirements Not Met'
            : twitterVerifyStatus === 'verifying'
              ? 'Verifying...'
              : !twitterFollowClicked
                ? 'Follow on X to Continue'
                : 'Enter X Username to Register'}
        </Button>
      ) : (
        <Button disabled className="w-full">
          {phase.closedReason || 'Registration Closed'}
        </Button>
      )}
    </div>
  );
}

function RequirementRow({
  requirement,
  isRegistered,
  twitterUsername,
  onTwitterUsernameChange,
  twitterFollowClicked,
  onTwitterFollowClick,
  twitterVerifyStatus,
  onTwitterUsernameBlur,
}: {
  requirement: RequirementCheckResult;
  isRegistered: boolean;
  twitterUsername: string;
  onTwitterUsernameChange: (value: string) => void;
  twitterFollowClicked: boolean;
  onTwitterFollowClick: () => void;
  twitterVerifyStatus: 'pending' | 'verifying' | 'verified';
  onTwitterUsernameBlur: () => void;
}) {
  // Don't render paid_registration as a checklist item — it has its own UI
  if (requirement.type === 'paid_registration') return null;

  const { label, actionUrl, needsInput, inputField } = requirement;
  // For twitter_follow: pending (X) -> verifying (spinner) -> verified (checkmark)
  // But if already registered, treat twitter as passed
  const isTwitter = requirement.type === 'twitter_follow';
  const passed = isTwitter
    ? (isRegistered || twitterVerifyStatus === 'verified' ? true : false)
    : requirement.passed;
  const isVerifying = isTwitter && !isRegistered && twitterVerifyStatus === 'verifying';
  const description = (isTwitter && isRegistered) ? 'Requirement met' : requirement.description;

  return (
    <div>
      <div className="flex items-start gap-2.5">
        {/* Status icon */}
        {isVerifying ? (
          <svg className="w-[18px] h-[18px] mt-0.5 text-brand-primary flex-shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : passed == null ? (
          <div className="w-[18px] h-[18px] mt-0.5 rounded-full border-2 border-neutral-border flex-shrink-0" />
        ) : passed ? (
          <svg className="w-[18px] h-[18px] mt-0.5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-[18px] h-[18px] mt-0.5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {actionUrl && requirement.type === 'twitter_follow' && !isRegistered ? (
            <a
              href={actionUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onTwitterFollowClick}
              className="text-body font-body text-brand-primary hover:underline"
            >
              {label}
            </a>
          ) : (
            <span className="text-body font-body text-default-font">
              {label}
            </span>
          )}
          {description && (
            <span className={`text-caption font-caption block mt-0.5 ${passed ? 'text-green-600' : 'text-subtext-color'}`}>
              {description}
            </span>
          )}
          {actionUrl && passed === false && requirement.type !== 'twitter_follow' && (
            <a
              href={actionUrl}
              className="text-caption font-caption text-brand-primary hover:underline mt-0.5 inline-block"
            >
              View &rarr;
            </a>
          )}
        </div>
      </div>

      {/* Twitter username input — only show after they've clicked the follow link */}
      {needsInput && inputField?.key === 'twitter_username' && twitterFollowClicked && !isRegistered && (
        <div className="mt-2 ml-7">
          <label className="text-caption font-caption text-subtext-color block mb-1">
            {inputField.label}
          </label>
          <div className="relative max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-subtext-color text-body font-body">@</span>
            <input
              type="text"
              value={twitterUsername}
              onChange={(e) => onTwitterUsernameChange(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
              onBlur={onTwitterUsernameBlur}
              placeholder={inputField.placeholder}
              maxLength={50}
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              className="w-full pl-8 pr-3 py-2 rounded-lg border border-neutral-border bg-default-background text-body font-body text-default-font placeholder:text-subtext-color"
            />
          </div>
        </div>
      )}
    </div>
  );
}
